import { makePlanLongId } from ".";
import {
  UserLanguage,
  FullPlanId,
  MctToastType,
  PlanDetailsPageSection,
  IdParts,
} from "../@types";
import routes from "../app/routes";
import URI from "urijs";

const makeFullUrl = (
  baseRoute: string,
  language: UserLanguage,
  year: string,
  section: string
): string => {
  return `${baseRoute}?year=${year}&lang=${language}#${section}`;
};

export const getPlanDetailsStarRatingsRoute = (
  plan: FullPlanId,
  language: UserLanguage,
  year: string
): string => {
  return makeFullUrl(
    routes.planDetails.replace(":id", makePlanLongId(plan)),
    language,
    year,
    PlanDetailsPageSection.STAR_RATINGS
  );
};

export const getToastRoute = (
  currentRoute: string,
  toastType: MctToastType
): string => {
  const curr = new URI(currentRoute);
  curr.addQuery("mctt", toastType);

  return curr.toString();
};

export const getRouteName = (r: string): string => {
  r = r.replace(/\/MEDIGAP_PLAN_TYPE_.+/, "");
  r = r.replace(/\/\d.+/, "");
  r = r.replace(/states\/[A-Z]{2}/, "states/");

  const routeNames = {
    [routes.coverageWizard.info]: "Coverage Wizard - Info",
    [routes.coverageWizard.landingPage]: "Coverage Wizard - Landing",
    [routes.coverageWizard.options]: "Coverage Wizard - Options",
    [routes.pap.landingPage]: "PAP - Home",
    [routes.pap.plans.replace("/:drug", "")]: "PAP - Results",
    [routes.pace.landingPage]: "PACE - Landing",
    [routes.pace.plans.replace(":state", "")]: "PACE - Plans",
    [routes.medigap.landingPage]: "Medigap - Find a Policy",
    [routes.medigap.planDetails.replace(
      "/:medigapPlanType",
      ""
    )]: "Medigap - Plan Details",
    [routes.medigap.plans]: "Step 1: Decide what plans you want",
    [routes.medigap.policies.replace(
      "/:medigapPlanType",
      ""
    )]: "Step 2: Pick your Policy",
    [routes.managePrescriptions]: "Find Plans - Add Drugs",
    [routes.prescriptionsList]: "Find Plans - Add Drugs",
    [routes.pharmacy]: "Find Plans - Add Pharmacy",
    [routes.searchResults]: "Find Plans - Search Results",
    [routes.questionRouting]: "Find Plans - Enter Your Information",
    [routes.drugSearchPreferences]: "Find Plans - See Drug Costs",
    [routes.enroll]: "OEC - Enrollment",
    [routes.planDetails.replace("/:id", "")]: "Find Plans - Plan Details",
    [routes.sanctionedPlans]: "Find Plans - Sanctioned Plans",
    [routes.spap.landingPage]: "SPAP - Home",
    [routes.spap.plans.replace(":state", "")]: "SPAP - Results",
    [routes.pdePage]: "Find Plans - LIS Select Drugs",
    ["/"]: "Find Plans - Landing",
  };

  return routeNames[r] || r;
};

export const getRedirectForMedigapOrPlanFinder = (url: URL): string | null => {
  let redirectUrl = null;

  const medigapPath = "medigap-supplemental-insurance-plans";
  const planComparePath = "plan-compare";

  const medigapRoutes = Object.values(routes.medigap).map(route => {
    const matches = route.match(/m\/(.*?)\//);
    const value = matches ? `/m/${matches[1]}` : route;

    return value;
  });

  // Get a list of all non-medigap routes (some of which are in nested objects)
  // and replace their tokens with empty strings
  const planCompareRoutes: unknown[] = [];
  Object.keys(routes)
    .filter((r): r is string => r !== "medigap")
    .forEach(route => {
      if (typeof routes[route] === "string") {
        planCompareRoutes.push(routes[route].replace("/:id", ""));
      } else {
        Object.values(routes[route]).forEach(r =>
          planCompareRoutes.push((r as string).replace(/:state|:drug/, ""))
        );
      }
    });

  const { pathname, hash } = url;

  // Remove the #, query string, and additional "/" after m/route
  let formattedHash = hash.replace("#", "").replace(/\?.+/gm, "");

  const matches = formattedHash.match(/m\/(.*?)\//);
  formattedHash = matches ? `/m/${matches[1]}` : formattedHash;

  const strippedPath = pathname.replace(/\//gm, "");

  if (
    strippedPath === medigapPath &&
    planCompareRoutes.includes(formattedHash)
  ) {
    redirectUrl = `${url.origin}/${planComparePath}/${url.hash}`;
  } else if (
    strippedPath === planComparePath &&
    medigapRoutes.includes(formattedHash)
  ) {
    redirectUrl = `${url.origin}/${medigapPath}/${url.hash}`;
  } else if (
    strippedPath === medigapPath &&
    !medigapRoutes.includes(formattedHash)
  ) {
    redirectUrl = `${url.origin}/${medigapPath}/#/m`;
  }

  return redirectUrl;
};

/**
 * Get the first segment of a route path, no slashes and no params
 * @param path - A path listed in app routes, which may contain slash-delimited
 *               segments and params
 * @returns - The first segment of the path, only
 */
export const getBasePath = (path: string): string =>
  path.replace(/^\/*([\d|\w|\-|_]*).*$/, (_match, p1) => p1);

/**
 * Determine whether the current route should display the Consistent Header
 * @param location - A location object passed from a routed component, e.g.
 * @param hideGlobalHeaderPaths - An array of route paths for routes that shouldn't
 *                                show the Global (Consistent) Header
 * @returns - Whether the current route should display the CH
 */
export const shouldDisplayGlobalHeader = (
  location: Partial<Location>,
  hideGlobalHeaderPaths: string[]
): boolean =>
  !hideGlobalHeaderPaths
    .map(getBasePath)
    .some(path => location?.pathname?.includes(path));

/**
 * Create a link to plan details interpolating the :id variable with a plan long id
 * and adding needed query parameters
 * @param plan
 * @param language
 * @returns A formatted link to plan details
 */
export const makePlanDetailsUrl = ({
  plan,
  yearValue = "contract_year",
  language,
}: {
  plan: IdParts;
  yearValue?: "contract_year" | "coverage_year";
  language: UserLanguage;
}): string =>
  `${routes.planDetails.replace(
    ":id",
    makePlanLongId(plan)
  )}?lang=${language}&year=${plan[yearValue]}`;
